<template>
  <section v-if="showHeader" class="flex flex-col mt-4 sm:rounded-lg bg-slate-50 dark:bg-zinc-800 2xl:max-w-6xl 2xl:mx-auto">
    <RequestContentInfo />
    <SmartActions v-if="type === ItemType.VIDEO && hasAiAddon" />
    <ItemDescription />
    <Chapters />
    <div v-if="showFeedback" class="flex items-center px-4 pb-4">
      <Feedback
        :feedback="feedbackState"
        :aiProvider="descriptionProvider"
        :additionalTrackingProps="isSmartActionDescription ? { smart_action_type: smartActionsStore.actionType } : {}"
        @feedback="handleFeedback"
      />
    </div>
  </section>
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia';

import { useCallToActionStore } from '../store/useCallToActionStore';
import { useViewerItemStore, ItemType } from '../store/useViewerItemStore';
import { useAiMetadataStore, AiMetadataStatus } from '../store/useAiMetadataStore';
import { useSmartActionsStore, AiSmartActionStatus } from '../store/useSmartActionsStore';
import { useAddon } from '@/modules/viewer/composables/useAddon';
import { useUser } from '../composables/useUser';

import ItemDescription from './Description.vue';
import Chapters from './Chapters.vue';
import RequestContentInfo from './RequestContentInfo.vue';
import SmartActions from './smart_actions/SmartActions.vue';
import Feedback from '@/storybook/templates/smart_actions/Feedback.vue';
import { computed, watch } from 'vue';

const { hasAiAddon } = useAddon();

const itemStore = useViewerItemStore();
const { description, requestContentId, type } = storeToRefs(itemStore);

const ctaStore = useCallToActionStore();
const { isHoverable, displayActionActive } = storeToRefs(ctaStore);

const aiMetadataStore = useAiMetadataStore();
const { hasChapters } = storeToRefs(aiMetadataStore);

const smartActionsStore = useSmartActionsStore();

const { isCurrentItemOwner } = useUser();

const showHeader = computed(
  () =>
    isCurrentItemOwner.value || description.value?.length || (!isHoverable.value && displayActionActive.value) || hasChapters.value || requestContentId?.value
);

// Determine if description is from smart actions or AI metadata
const isSmartActionDescription = computed(() => {
  return !!smartActionsStore.actionType && smartActionsStore.actionStatus === AiSmartActionStatus.DONE;
});

const isAiMetadataDescription = computed(() => {
  return !isSmartActionDescription.value && aiMetadataStore.status === AiMetadataStatus.DONE;
});

// Get the appropriate provider based on description source
const descriptionProvider = computed(() => {
  if (isSmartActionDescription.value) {
    return smartActionsStore.provider || 'openai';
  }
  if (isAiMetadataDescription.value) {
    return aiMetadataStore.provider || 'sieve';
  }
  return null;
});

// Get the appropriate feedback state
const feedbackState = computed(() => {
  if (isSmartActionDescription.value) {
    return smartActionsStore.feedback;
  }
  if (isAiMetadataDescription.value) {
    return aiMetadataStore.feedback;
  }
  return null;
});

function handleFeedback(type: 'positive' | 'negative' | null) {
  if (isSmartActionDescription.value) {
    smartActionsStore.setSmartActionFeedback(type);
  } else if (isAiMetadataDescription.value) {
    aiMetadataStore.setAiMetadataFeedback(type);
  }
}

const showFeedback = computed(() => {
  return isCurrentItemOwner.value && (isSmartActionDescription.value || isAiMetadataDescription.value) && description.value && description.value.length > 0;
});

// Reset feedback when smart action type changes (user regenerates smart action)
watch(
  () => smartActionsStore.actionType,
  () => {
    smartActionsStore.setSmartActionFeedback(null);
  }
);

// Reset feedback when AI metadata status changes (user regenerates summary)
watch(
  () => aiMetadataStore.status,
  (newStatus, oldStatus) => {
    // Only reset when status changes to 'working' (regeneration started)
    if (oldStatus === AiMetadataStatus.DONE && newStatus === AiMetadataStatus.WORKING) {
      aiMetadataStore.setAiMetadataFeedback(null);
    }
  }
);
</script>
